import { domainError, type DomainError } from '../shared/domain-error';
import { compareNames, normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { Money } from '../shared/money';
import type { EmployeeId, JobRoleId, ProjectId, WorkCalendarId } from '../shared/ids';

/**
 * Funcionário da obra.
 *
 * O cadastro é por obra (RN-050): o mesmo CPF pode aparecer em duas obras da mesma
 * construtora, e isso não é duplicidade — é a mesma pessoa em dois canteiros.
 *
 * Todo funcionário tem um `ExecutionResource` criado na mesma transação (RN-052). Sem
 * isso existiria funcionário invisível ao planejamento, que é pior do que funcionário
 * inexistente: ele aparece na lista e some na hora de alocar.
 */
export type EmployeeStatus = 'ACTIVE' | 'ON_LEAVE' | 'TERMINATED';

export const EMPLOYEE_STATUS_LABEL: Readonly<Record<EmployeeStatus, string>> = {
  ACTIVE: 'Ativo',
  ON_LEAVE: 'Afastado',
  TERMINATED: 'Desligado',
};

/**
 * Transições permitidas.
 *
 * Desligado volta para ativo de propósito: recontratação acontece, e obrigar a cadastrar
 * de novo criaria um segundo registro para a mesma pessoa — e um CPF duplicado.
 */
const ALLOWED_TRANSITIONS: Readonly<Record<EmployeeStatus, readonly EmployeeStatus[]>> = {
  ACTIVE: ['ON_LEAVE', 'TERMINATED'],
  ON_LEAVE: ['ACTIVE', 'TERMINATED'],
  TERMINATED: ['ACTIVE'],
};

/** O teto dos encargos: acima de 300% sobre o salário é número digitado errado, e não regime. */
export const EMPLOYEE_CHARGES_MAX_PERCENT = 300;

export const EMPLOYEE_ERROR = {
  NAME_REQUIRED: 'WORKFORCE.EMPLOYEE_NAME_REQUIRED',
  INVALID_STATUS_TRANSITION: 'WORKFORCE.EMPLOYEE_INVALID_TRANSITION',
  REGISTRATION_TAKEN: 'WORKFORCE.REGISTRATION_TAKEN',
  NOT_FOUND: 'WORKFORCE.EMPLOYEE_NOT_FOUND',
} as const;

export interface Employee {
  readonly id: EmployeeId;
  readonly projectId: ProjectId;
  readonly name: string;
  /** Só dígitos (RN-050). Ausente = cadastro sem CPF, permitido para diarista. */
  readonly cpf?: string;
  /** Matrícula da folha, única na obra (RN-051). */
  readonly registration?: string;
  readonly jobRoleId: JobRoleId;
  /**
   * Salário do mês, em centavos (RN-059).
   *
   * É o que a folha paga e o que o escritório sabe informar. O custo por hora **não** é
   * cadastrado: o sistema o calcula dividindo isto pelas horas úteis do mês, que saem do
   * calendário de trabalho — ver `labor-cost.ts`.
   *
   * Ausente = indicador de custo indisponível, nunca zero (RN-055).
   */
  readonly monthlySalary?: Money;
  /**
   * Os encargos sobre o salário, em % (Fase 29, D12): INSS patronal, FGTS, férias, 13º — o que a
   * folha custa além do salário. Um por funcionário, porque o regime de cada um muda a conta.
   *
   * Ausente = não informado, e a conta segue com o salário puro: o custo existe, só está
   * subestimado, e a tela diz isso. Diferente do salário ausente, que deixa o custo
   * indisponível (RN-055).
   */
  readonly chargesPercent?: number;
  readonly status: EmployeeStatus;
  readonly admittedAt?: string;
  readonly terminatedAt?: string;
  /** Calendário próprio; ausente = o da obra, depois o do sistema (RN-040). */
  readonly workCalendarId?: WorkCalendarId;
  readonly phone?: string;
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export interface CreateEmployeeInput {
  readonly id: EmployeeId;
  readonly projectId: ProjectId;
  readonly name: string;
  readonly jobRoleId: JobRoleId;
  readonly cpf?: string;
  readonly registration?: string;
  readonly monthlySalary?: Money;
  readonly chargesPercent?: number;
  readonly admittedAt?: string;
  readonly workCalendarId?: WorkCalendarId;
  readonly phone?: string;
  readonly notes?: string;
  readonly now: string;
}

export function createEmployee(input: CreateEmployeeInput): Result<Employee, DomainError> {
  const name = input.name.trim();
  if (name.length === 0) {
    return err(domainError(EMPLOYEE_ERROR.NAME_REQUIRED, 'O nome do funcionário é obrigatório.'));
  }

  return ok({
    id: input.id,
    projectId: input.projectId,
    name,
    jobRoleId: input.jobRoleId,
    status: 'ACTIVE',
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.cpf === undefined ? {} : { cpf: input.cpf }),
    ...(input.registration === undefined ? {} : { registration: input.registration }),
    ...(input.monthlySalary === undefined ? {} : { monthlySalary: input.monthlySalary }),
    ...(input.chargesPercent === undefined ? {} : { chargesPercent: input.chargesPercent }),
    ...(input.admittedAt === undefined ? {} : { admittedAt: input.admittedAt }),
    ...(input.workCalendarId === undefined ? {} : { workCalendarId: input.workCalendarId }),
    ...(input.phone === undefined ? {} : { phone: input.phone }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  });
}

export function canTransition(from: EmployeeStatus, to: EmployeeStatus): boolean {
  return from === to || (ALLOWED_TRANSITIONS[from]?.includes(to) ?? false);
}

/**
 * Muda a situação.
 *
 * `TERMINATED` carimba a data do desligamento; voltar para ativo a limpa — quem foi
 * recontratado não está desligado desde ontem.
 */
export function changeEmployeeStatus(
  employee: Employee,
  status: EmployeeStatus,
  now: string,
  date?: string,
): Result<Employee, DomainError> {
  if (!canTransition(employee.status, status)) {
    return err(
      domainError(
        EMPLOYEE_ERROR.INVALID_STATUS_TRANSITION,
        `Não é possível passar de ${EMPLOYEE_STATUS_LABEL[employee.status]} para ${EMPLOYEE_STATUS_LABEL[status]}.`,
      ),
    );
  }

  const { terminatedAt: _dropped, ...rest } = employee;

  return ok({
    ...rest,
    status,
    updatedAt: now,
    ...(status === 'TERMINATED' ? { terminatedAt: date ?? now.slice(0, 10) } : {}),
  });
}

/** Chave natural do CPF na obra (RN-050). */
export function employeeCpfKey(projectId: ProjectId, cpf: string): string {
  return `${projectId}|${cpf.replace(/\D/g, '')}`;
}

/** Chave natural da matrícula na obra (RN-051). */
export function employeeRegistrationKey(projectId: ProjectId, registration: string): string {
  return `${projectId}|${normalizeName(registration)}`;
}

export function compareEmployees(a: Employee, b: Employee): number {
  return compareNames(a.name, b.name);
}

/** Só quem está ativo entra no planejamento; afastado e desligado ficam no cadastro. */
export function isAllocatable(employee: Employee): boolean {
  return employee.status === 'ACTIVE' && employee.deletedAt === undefined;
}
